import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme, Theme } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Default images if none are provided
const defaultImages: string[] = [
  '/img/clinic/dexas.png',
  'https://placehold.co/600x300/E2E8F0/4A5568?text=Full+View+1',
  'https://placehold.co/600x300/CBD5E0/2D3748?text=Full+View+2',
  'https://placehold.co/600x300/A0AEC0/1A202C?text=Full+View+3',
  'https://placehold.co/600x300/718096/F7FAFC?text=Full+View+4',
  'https://placehold.co/600x300/4A5568/E2E8F0?text=Full+View+5',
];

/**
 * Props for the InteractiveHorizontalCarousel component.
 */
interface InteractiveHorizontalCarouselProps {
  /** Array of image URLs. Defaults to placeholder images. */
  images?: string[];
  /**
   * Intended width of images, primarily for aspect ratio considerations if height is also dynamic.
   * In this mode, the image will stretch to viewport width. Defaults to 300.
   */
  imageWidth?: number; // This prop is less about display width now
  /** Height of the image area in pixels. Defaults to 200. */
  imageHeight?: number;
}

/**
 * InteractiveHorizontalCarousel component (MUI Styled - Single Image Mode)
 * Displays one image at a time, filling the carousel's viewport width.
 * Users can scroll through using buttons.
 */
const InteractiveHorizontalCarousel: React.FC<InteractiveHorizontalCarouselProps> = ({
  images = defaultImages,
  imageWidth = 300, // Kept for potential aspect ratio use, but display width is viewportWidth
  imageHeight = 200,
}) => {
  const theme: Theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateViewportWidth = () => {
      if (viewportRef.current) {
        setViewportWidth(viewportRef.current.offsetWidth);
      }
    };

    // Set initial width and add resize listener
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);

    // Cleanup listener on component unmount
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

  const handleNext = () => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, images.length - 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  // Calculate the x offset for the motion.div.
  // Each image takes the full viewportWidth.
  const xOffset = -(currentIndex * viewportWidth);

  // Total width of the motion.div (the scrollable content)
  // Each image item will take the full viewportWidth.
  const motionDivWidth = images.length * viewportWidth;

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        py: 2, // Vertical padding for the carousel section
      }}
    >
      {/* This Box is the viewport and clips the motion.div */}
      <Box
        ref={viewportRef}
        sx={{
          width: '100%', // Takes full width of its parent
          overflow: 'hidden', // Crucial for clipping
          height: `${imageHeight}px`, // Set the height of the viewport
          // border: '1px solid red', // For debugging viewport
        }}
      >
        {/* Render motion.div only when viewportWidth is known to prevent initial render issues */}
        {viewportWidth > 0 && (
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              width: `${motionDivWidth}px`, // Total width of all images side-by-side
              height: '100%', // Takes full height of the viewport Box
              // border: '1px solid blue', // For debugging motion.div
            }}
            animate={{ x: xOffset }}
            transition={{ type: 'spring', stiffness: 200, damping: 25 }}
          >
            {images.map((src: string, index: number) => (
              <Box
                key={index}
                sx={{
                  flexShrink: 0, // Prevent items from shrinking
                  width: `${viewportWidth}px`, // Each image item takes the full viewport width
                  height: '100%', // Takes full height of the motion.div (which is viewport height)
                  borderRadius: theme.shape.borderRadius, // Optional: if you want rounded corners on the image itself
                  overflow: 'hidden', // Optional: if image itself has border-radius and object-fit might exceed
                  // border: '1px solid green', // For debugging individual items
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={`Carousel image ${index + 1}`}
                  sx={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover', // 'cover' will fill, cropping if necessary. 'contain' would letterbox.
                    display: 'block', // Removes potential extra space below img
                  }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src="https://placehold.co/600x300/FF0000/FFFFFF?text=Error";
                  }}
                />
              </Box>
            ))}
          </motion.div>
        )}
      </Box>

      {/* Navigation Buttons - show if there's more than one image */}
      {images.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            disabled={currentIndex === 0}
            sx={{
              position: 'absolute',
              top: '50%',
              // Adjust left positioning to be inside or slightly overlapping the edge
              left: { xs: theme.spacing(1), sm: theme.spacing(2) },
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255, 255, 255, 0.5)', // Slightly more subtle
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.75)',
              },
              zIndex: 2, // Ensure buttons are above the images
              boxShadow: theme.shadows[1], // Lighter shadow
              transition: 'background-color 0.2s ease',
            }}
            aria-label="previous image"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={currentIndex === images.length - 1}
            sx={{
              position: 'absolute',
              top: '50%',
              // Adjust right positioning
              right: { xs: theme.spacing(1), sm: theme.spacing(2) },
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.75)',
              },
              zIndex: 2,
              boxShadow: theme.shadows[1],
              transition: 'background-color 0.2s ease',
            }}
            aria-label="next image"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default InteractiveHorizontalCarousel;
